import { useRef, useState } from "react";
import axios from "axios";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { toast } from "sonner";
import {
  Star, Radar, ShieldCheck, BellRing, CloudSun, WifiOff, Route,
  DoorOpen, Car, ScanLine, Armchair, Send,
} from "lucide-react";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import Simulator from "@/components/Simulator";
import ReviewTicker from "@/components/ReviewTicker";
import CtaBanner from "@/components/CtaBanner";
import StoreBadges from "@/components/StoreBadges";
import AppShowcase from "@/components/AppShowcase";
import { MaskedLine } from "@/components/MaskedLine";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BENTO = [
  {
    icon: Route, badge: "AI CORE", title: "Smart Leave-Time Engine",
    desc: "A proprietary AI cross-analyzes live highway speeds, airport curb congestion, and TSA queues to hand you one exact departure timestamp.",
    span: "lg:col-span-7",
  },
  {
    icon: ShieldCheck, badge: "REAL-TIME", title: "Live TSA & Terminal Radar",
    desc: "Know standard vs PreCheck lane wait times before you even step into your ride.",
    span: "lg:col-span-5",
  },
  {
    icon: BellRing, badge: "ZERO MISS", title: "Gate Change Alerts",
    desc: "Instant push notifications when your gate shifts or your inbound aircraft runs late.",
    span: "lg:col-span-4",
  },
  {
    icon: CloudSun, badge: "METAR DATA", title: "Hyperlocal Weather Insight",
    desc: "Destination forecasts and runway visibility updates, factored into your timeline.",
    span: "lg:col-span-4",
  },
  {
    icon: WifiOff, badge: "ALWAYS ON", title: "Offline Trip Cache",
    desc: "Boarding passes, parking pins, and gate numbers, accessible even without signal.",
    span: "lg:col-span-4",
  },
];

const JOURNEY = [
  { icon: DoorOpen, step: "01", t: "T-180 MIN", title: "The leave-time alert", detail: "Tripsee watches traffic build and tells you the precise minute to lock your front door." },
  { icon: Car, step: "02", t: "T-130 MIN", title: "The fastest route out", detail: "Live highway intelligence routes you around bottlenecks to the quickest drop-off zone." },
  { icon: ScanLine, step: "03", t: "T-75 MIN", title: "The shortest security line", detail: "Real-time queue meters point you to the fastest TSA checkpoint in your terminal." },
  { icon: Armchair, step: "04", t: "T-30 MIN", title: "Coffee before boarding", detail: "You reach the gate with time to spare: calm, seated, and first in your boarding group." },
];

const REVIEWS = [
  {
    quote: "It told me to leave 35 minutes earlier than my maps app suggested, and it was right. I cleared security while the freeway behind me locked up.",
    author: "Frequent Flyer", role: "Consultant · 80 segments a year", rating: 5,
  },
  {
    quote: "The first app that actually factors in TSA lines and the walk to the gate. No more sprinting through the terminal.",
    author: "Business Traveler", role: "Sales director, on the road weekly", rating: 5,
  },
  {
    quote: "I used to arrive three hours early just in case. Now I arrive calm, with time for one good coffee before boarding.",
    author: "Nervous Traveler", role: "Now a relaxed one", rating: 5,
  },
];

function TiltCard({ children }) {
  const ref = useRef(null);
  const rx = useSpring(useMotionValue(0), { stiffness: 140, damping: 22 });
  const ry = useSpring(useMotionValue(0), { stiffness: 140, damping: 22 });

  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    ry.set(px * 8);
    rx.set(-py * 8);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => { rx.set(0); ry.set(0); }}
      style={{ rotateX: rx, rotateY: ry, transformPerspective: 1000 }}
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="animate-float-slow"
    >
      {children}
    </motion.div>
  );
}

function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("You're on the list. Travel smart.");
      setEmail("");
    } catch {
      toast.error("Couldn't subscribe right now. Try again shortly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
      <input
        data-testid="newsletter-email-input"
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        className="flex-1 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-primary/50 transition-colors"
      />
      <button
        data-testid="newsletter-submit-button"
        type="submit"
        disabled={busy}
        className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3.5 text-sm font-bold text-[#0B0B0D] transition-transform duration-200 hover:scale-[1.03] active:scale-95 disabled:opacity-60"
      >
        <Send size={15} /> {busy ? "Joining…" : "Subscribe"}
      </button>
    </form>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. We'll get back to you.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Couldn't send right now. Email us at support@tripsee.io.");
    } finally {
      setBusy(false);
    }
  };

  const field = "w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-primary/50 transition-colors";

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input
          data-testid="contact-name-input"
          required
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Your name"
          className={field}
        />
        <input
          data-testid="contact-email-input"
          type="email"
          required
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="Your email"
          className={field}
        />
      </div>
      <textarea
        data-testid="contact-message-input"
        required
        rows={4}
        value={form.message}
        onChange={(e) => setForm({ ...form, message: e.target.value })}
        placeholder="How has Tripsee changed your travel day?"
        className={field}
      />
      <button
        data-testid="contact-submit-button"
        type="submit"
        disabled={busy}
        className="w-full rounded-xl bg-white/10 border border-white/15 py-3.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-primary hover:text-[#0B0B0D] hover:border-primary disabled:opacity-60"
      >
        {busy ? "Sending…" : "Drop us a line"}
      </button>
    </form>
  );
}

export default function Home() {
  return (
    <main>
      <Seo
        title="Tripsee — Stop Guessing. Plan Smart. Fly On Time."
        description="Tripsee is the AI door-to-gate travel companion that fuses live flight, traffic, TSA wait time, and weather data into one precise leave-home time."
      />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-radar-grid pt-16">
        <div className="absolute inset-0 ambient-glow" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0B0B0D]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center w-full">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2.5 rounded-full border border-primary/30 bg-primary/[0.07] px-4 py-2 mb-8"
            >
              <Radar size={14} className="text-primary" />
              <span className="font-mono text-[11px] tracking-[0.25em] text-primary uppercase">
                Door-to-Gate Travel Companion
              </span>
            </motion.div>

            <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.05] text-white">
              <MaskedLine delay={0.1}>Stop guessing.</MaskedLine>
              <MaskedLine delay={0.24}>Plan smart.</MaskedLine>
              <MaskedLine delay={0.38} className="text-primary text-glow">Fly on time.</MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.65 }}
              className="mt-7 text-lg sm:text-xl text-slate-400 leading-relaxed max-w-2xl"
            >
              Tripsee fuses live flight, traffic, TSA, and weather intelligence into one precise
              departure time, so you leave home at exactly the right minute and never waste hours
              at the gate.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.8 }}
              className="mt-10 flex flex-col sm:flex-row items-start gap-4"
            >
              <StoreBadges testIdPrefix="hero" />
              <a
                href="#capabilities"
                data-testid="hero-cta-demo-button"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-8 py-4 text-base font-semibold text-slate-200 hover:border-primary/50 hover:text-primary transition-colors duration-200"
              >
                See it in action
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="mt-10 flex items-center gap-3"
            >
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={15} className="text-[#FFB100] fill-[#FFB100]" />
                ))}
              </div>
              <span className="text-sm text-slate-400">Loved by frequent flyers on iOS & Android</span>
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <TiltCard>
              <Simulator />
            </TiltCard>
          </div>
        </div>
      </section>

      <Marquee />

      {/* BENTO */}
      <section id="capabilities" className="relative py-28 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs tracking-[0.3em] text-primary uppercase mb-5">Capabilities</p>
            <h2 className="font-display text-2xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-white">
              Every variable between your door and your gate, handled.
            </h2>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-6">
            {BENTO.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.08} className={`col-span-1 ${b.span}`}>
                <div
                  data-testid={`bento-card-${i}`}
                  className="group h-full glass-panel rounded-2xl p-8 transition-all duration-300 hover:bg-[#161D2E]/80 hover:border-primary/30 hover:-translate-y-1"
                >
                  <div className="flex items-center justify-between">
                    <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/25 flex items-center justify-center text-primary">
                      <b.icon size={20} />
                    </div>
                    <span className="font-mono text-[10px] tracking-[0.2em] text-slate-500 border border-white/10 rounded-full px-3 py-1">
                      {b.badge}
                    </span>
                  </div>
                  <h3 className="mt-6 font-display text-xl sm:text-2xl font-bold text-white">{b.title}</h3>
                  <p className="mt-3 text-sm sm:text-base text-slate-400 leading-relaxed">{b.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* JOURNEY */}
      <section className="relative py-28 sm:py-32 bg-[#101315] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs tracking-[0.3em] text-primary uppercase mb-5">The Journey</p>
            <h2 className="font-display text-2xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-white">
              From your front door to 35,000 feet.
            </h2>
            <p className="mt-5 text-lg text-slate-400 leading-relaxed">
              One timeline orchestrates the four most stressful hours of travel.
            </p>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {JOURNEY.map((j, i) => (
              <Reveal key={j.step} delay={i * 0.1}>
                <div data-testid={`journey-step-${i}`} className="relative h-full glass-panel rounded-2xl p-7 hover:border-primary/30 transition-colors duration-300">
                  <span className="font-mono text-[11px] tracking-widest text-[#FFB100]">{j.t}</span>
                  <div className="mt-4 w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-primary">
                    <j.icon size={20} />
                  </div>
                  <h3 className="mt-5 font-display text-lg font-bold text-white">{j.title}</h3>
                  <p className="mt-2.5 text-sm text-slate-400 leading-relaxed">{j.detail}</p>
                  <span className="absolute top-7 right-7 font-mono text-3xl font-bold text-white/[0.07]">{j.step}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* APP SHOWCASE */}
      <AppShowcase />

      {/* REVIEWS */}
      <section className="relative py-28 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="font-mono text-xs tracking-[0.3em] text-primary uppercase mb-5">Live From The App Store</p>
            <h2 className="font-display text-2xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-white">
              Travelers begin their trip calmer with Tripsee.
            </h2>
          </Reveal>

        </div>
        <div className="mt-16">
          <ReviewTicker fallback={REVIEWS} />
        </div>
      </section>

      {/* NEWSLETTER + CONTACT */}
      <section className="relative py-28 sm:py-32 bg-[#101315] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Reveal>
            <div className="h-full glass-panel rounded-2xl p-8 sm:p-10">
              <p className="font-mono text-xs tracking-[0.3em] text-primary uppercase mb-5">Newsletter</p>
              <h3 className="font-display text-2xl sm:text-3xl font-bold text-white leading-snug">
                Travel intelligence, straight to your inbox.
              </h3>
              <p className="mt-4 text-slate-400 leading-relaxed">
                Announcements, new features, and airport hacks from the Tripsee crew. No spam, ever.
              </p>
              <div className="mt-8">
                <NewsletterForm />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="h-full glass-panel rounded-2xl p-8 sm:p-10">
              <p className="font-mono text-xs tracking-[0.3em] text-primary uppercase mb-5">Contact</p>
              <h3 className="font-display text-2xl sm:text-3xl font-bold text-white leading-snug">
                Or better yet, drop us a line.
              </h3>
              <p className="mt-4 text-slate-400 leading-relaxed">
                We love hearing how Tripsee changed your travel day and what to build next.
              </p>
              <div className="mt-8">
                <ContactForm />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBanner />
    </main>
  );
}
